import React from 'react';
import * as Yup from 'yup';
import { Button, Dialog, DialogProps, Group } from '@economic/taco';
import classNames from 'classnames';
import { Trans } from '@lingui/macro';
import { Form, Field } from '../../shared/components/Form';
import { FormProps } from '../types';
import AccessDropdown from '../../shared/components/ui/AccessDropdown';
import internalApiClient from '../../shared/util/api/internalApiClient';
import { t } from '../../shared/components/l10n/l10n';
import { Department } from '../../views/Departments';

interface CreateDepartmentFormProps extends FormProps<Department> {
    showSaveAndApply?: boolean;
}

const initialValues: Partial<Department> = {
    access: true,
    number: undefined,
    name: undefined,
    isDepartment: true,
};

const validationSchema = Yup.object({
    access: Yup.boolean().required(),
    number: Yup.number().required(),
    name: Yup.string().required(),
    isDepartment: Yup.boolean().required(),
});

function CreateDepartmentForm({
    initialData = initialValues,
    notifications,
    onSubmitted,
    onSubmitError,
    showSaveAndApply,
}: CreateDepartmentFormProps) {
    const handleSubmit = async values => {
        try {
            const response = await internalApiClient.post(`/departments`, values);
            return Promise.resolve(response);
        } catch (error) {
            error.validation = error.response.data;
            return Promise.reject(error);
        }
    };

    const titleStyleClass = classNames({ 'text-center': !showSaveAndApply });
    return (
        <Form
            initialData={{ ...initialData, isDepartment: true }}
            messages={notifications}
            onSubmit={handleSubmit}
            onSubmitted={onSubmitted}
            onSubmitError={onSubmitError}
            validationSchema={validationSchema}>
            <h1 className={titleStyleClass}>{t`FORM_DEPARTMENT_CREATETEXT`}</h1>
            <Field.Number label={t`FORM_FIELD_LABEL_NUMBER`} name="number" required />
            <Field.Text label={t`FORM_FIELD_LABEL_NAME`} name="name" required />
            <Field.Dropdown label={t`FORM_FIELD_LABEL_ACCESS`} name="access" as={AccessDropdown} />
            <Group className="justify-end">
                <Dialog.Close>
                    <Button>
                        <Trans>Cancel</Trans>
                    </Button>
                </Dialog.Close>
                <Button type="submit" appearance="primary">
                    {showSaveAndApply ? <Trans>Save & apply</Trans> : <Trans>Save</Trans>}
                </Button>
            </Group>
        </Form>
    );
}

interface CreateDepartmentDialogProps extends Partial<DialogProps> {
    onCreate: (department: Department) => void;
    showSaveAndApply?: boolean;
}

export function CreateDepartmentDialog({ onCreate, showSaveAndApply, ...dialogProps }: CreateDepartmentDialogProps) {
    return (
        <Dialog size="xs" {...dialogProps}>
            <Dialog.Content aria-label={t`FORM_DEPARTMENT_CREATETEXT`}>
                <CreateDepartmentForm onSubmitted={onCreate} showSaveAndApply={showSaveAndApply} />
            </Dialog.Content>
        </Dialog>
    );
}
